/**
 * Requests Inbox — combined "awaiting me" view.
 *
 * Two sections: requests where I'm the current approver, and requests
 * assigned to me (by person or role) for fulfillment. Each row links to
 * the unified detail page.
 */

import { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'

type RequestListRow = {
  id: number
  code: string
  request_type_name: string
  request_type_icon: string
  requester_name: string
  summary: string
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'FULFILLED'
  current_step_name: string | null
  submitted_at: string | null
}

export default function RequestsInbox() {
  const navigate = useNavigate()

  const approvalsQ = useQuery({
    queryKey: ['requests', 'inbox', 'awaiting_me'],
    queryFn: async () =>
      (await api.get<RequestListRow[]>('/requests/?awaiting_me=true')).data,
  })
  const fulfillQ = useQuery({
    queryKey: ['requests', 'inbox', 'to_fulfill'],
    queryFn: async () =>
      (await api.get<RequestListRow[]>('/requests/?to_fulfill=true')).data,
  })

  const approvals = approvalsQ.data || []
  const fulfill = fulfillQ.data || []

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs">
          <Link to="/admin/requests" className="text-muted-foreground hover:text-foreground hover:underline">
            ← My Requests
          </Link>
        </div>
        <h2 className="text-2xl font-semibold text-foreground tracking-tight mt-2">Inbox</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Requests waiting on you — either for approval or to fulfill.
        </p>
      </div>

      {/* Approvals section */}
      <section className="mb-8">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Awaiting your approval ({approvals.length})
        </h3>
        {approvalsQ.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : approvals.length === 0 ? (
          <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            Nothing awaiting your approval.
          </div>
        ) : (
          <RequestTable rows={approvals} onClick={(id) => navigate(`/admin/requests/${id}`)} />
        )}
      </section>

      {/* Fulfillment section */}
      <section>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          To fulfill ({fulfill.length})
        </h3>
        {fulfillQ.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : fulfill.length === 0 ? (
          <div className="bg-card rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            Nothing assigned to you for fulfillment.
          </div>
        ) : (
          <RequestTable rows={fulfill} onClick={(id) => navigate(`/admin/requests/${id}`)} />
        )}
      </section>
    </AdminLayout>
  )
}

function RequestTable({ rows, onClick }: { rows: RequestListRow[]; onClick: (id: number) => void }) {
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Code</Th>
            <Th>Type</Th>
            <Th>Requester</Th>
            <Th>Summary</Th>
            <Th>Step</Th>
            <Th>Submitted</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr
              key={r.id}
              className="hover:bg-muted/40 cursor-pointer"
              onClick={() => onClick(r.id)}
            >
              <Td className="font-mono text-xs">{r.code}</Td>
              <Td className="text-xs">
                <span className="mr-1">{r.request_type_icon || '📝'}</span>
                {r.request_type_name}
              </Td>
              <Td className="text-sm">{r.requester_name}</Td>
              <Td className="text-sm font-medium max-w-[28rem] truncate">{r.summary}</Td>
              <Td className="text-xs text-muted-foreground">{r.current_step_name || '—'}</Td>
              <Td className="text-xs text-muted-foreground whitespace-nowrap">
                {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
